import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import NivaLogo from '../components/NivaLogo';
import { ROUTES } from '../constants/routes';
import { isTutorialCompleted } from '../utils/tutorialStorage';

export default function JoinScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { loginWithToken } = useAuth();
  const hospitalId = params.get('hospital') || '';
  const token = params.get('token') || '';
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [form, setForm] = useState({
    name: '',
    department_id: '',
    role: '',
    password: '',
    voice_improvement_consent: false,
  });

  useEffect(() => {
    if (!hospitalId || !token) {
      setError('This QR code is no longer valid. Please ask your administrator for a new QR code.');
      return;
    }
    apiGet(`/api/join/preview?hospital=${encodeURIComponent(hospitalId)}&token=${encodeURIComponent(token)}`)
      .then(setPreview)
      .catch((err) => setError(err.message || err.error?.detail || 'Could not open join link'));
  }, [hospitalId, token]);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    try {
      const data = await apiPost('/api/nurse/join', {
        hospital_id: hospitalId,
        token,
        name: form.name,
        department_id: form.department_id,
        role: form.role,
        password: form.password,
        voice_improvement_consent: form.voice_improvement_consent,
      });
      loginWithToken(data.token, data.username, {
        mustChangePassword: false,
        termsAccepted: data.terms_accepted ?? data.nurse?.terms_accepted,
      });
      setUsername(data.username);
    } catch (err) {
      const detail = err.error?.detail || err.message;
      setError(typeof detail === 'string' ? detail : 'Join failed');
    }
  };

  if (username) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sky-50 px-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-card">
          <NivaLogo className="mb-6" />
          <h1 className="text-xl font-bold">Save this username to log in</h1>
          <p className="mt-4 rounded-xl bg-primary-light px-4 py-3 text-center text-2xl font-mono">{username}</p>
          <button
            className="btn-primary mt-6"
            type="button"
            onClick={() => navigate(isTutorialCompleted() ? ROUTES.PATIENTS : ROUTES.ONBOARDING)}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-sky-50 px-4">
      <form onSubmit={submit} className="w-full max-w-md space-y-3 rounded-2xl bg-white p-8 shadow-card">
        <NivaLogo className="mb-4" />
        <h1 className="text-xl font-bold">Join {preview?.hospital_name || 'hospital'}</h1>
        {error && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        <input
          className="w-full rounded-xl border px-3 py-3"
          placeholder="Your name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />
        <select
          className="w-full rounded-xl border px-3 py-3"
          value={form.department_id}
          onChange={(e) => setForm({ ...form, department_id: e.target.value })}
          required
        >
          <option value="">Department</option>
          {(preview?.departments || []).map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        <input
          className="w-full rounded-xl border px-3 py-3"
          placeholder="Role"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
        />
        <input
          type="password"
          minLength={8}
          className="w-full rounded-xl border px-3 py-3"
          placeholder="Password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          required
        />
        <label className="flex items-start gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            className="mt-1"
            checked={form.voice_improvement_consent}
            onChange={(e) => setForm({ ...form, voice_improvement_consent: e.target.checked })}
          />
          I consent to storing de-identified transcripts and notes for quality improvement.
        </label>
        <button className="btn-primary" type="submit">Join</button>
      </form>
    </div>
  );
}
